import { Typography } from '@mui/material';
import { Metric, MetricType } from '@strength/domain';
import {
  applyBenchmark,
  applyInverseScale,
  calculateDelta,
  getMetricSpec,
  MetricBenchmarkData,
  WorkoutExerciseMetric,
  WorkoutPublishErrors,
  WorkoutSet,
  WorkoutSetMetric,
} from '@strength/domain-workout';
import { tZodIssue } from '@strength/i18n';
import { useTranslation } from 'react-i18next';
import { CollectionFlag } from '../../../components/icons/collection-flag';
import { RenderCellContentProps } from '../../../components/sheet/slots/metrics-slot';
import { cn } from '../../../components/utils';
import { ErrorContentWrapper } from './error-content-wrapper';

type Context = {
  workoutErrors: WorkoutPublishErrors | null;
  applyBenchmarkData: MetricBenchmarkData | undefined;
  libraryExerciseId: string;
  getMetric: (id: string) => Metric;
  actualSetMetric?: WorkoutSetMetric;
  profileId?: string;
};

export const SetMetricCellContent = ({
  row,
  metric,
  context,
  hideCollectionFlag,
  disabled,
}: RenderCellContentProps<
  WorkoutSet,
  WorkoutExerciseMetric,
  MetricType,
  Context
>) => {
  const className = cn(
    'flex items-center w-full h-full',
    ' py-2 pl-2 truncate',
  );

  const metricValue = row.metrics.find((m) => m.metricId === metric.metricId);
  const m = context.getMetric(metric.metricId);
  const spec = getMetricSpec(m);
  const metricValueInput = metricValue?.input ?? '';
  const valueParseResult = spec.schema.safeParse(metricValueInput);

  const showCollectionFlag = !!(
    !hideCollectionFlag && metricValue?.forCollection
  );

  const { t } = useTranslation();

  const {
    workoutErrors,
    libraryExerciseId,
    applyBenchmarkData,
    actualSetMetric,
  } = context;
  const errorsForCell =
    workoutErrors?.get(row.workoutSetId)?.map((err) => t(...err)) || [];
  const workoutHasErrors = errorsForCell.length > 0;

  if (!valueParseResult.success) {
    const showTooltip = workoutHasErrors || metricValueInput.length > 0;
    const errors = valueParseResult.error.issues
      .map((issue) => tZodIssue(issue, t))
      .concat(errorsForCell);
    return (
      <ErrorContentWrapper
        className={className}
        useTooltip={showTooltip}
        disabled={disabled}
        errors={errors}
        showCollectionFlag={showCollectionFlag}
        displayValue={metricValue?.input}
      />
    );
  }

  let value = valueParseResult.data;
  if (applyBenchmarkData) {
    // Routes intensity columns through the 1RM path and KPI-mapped columns
    // through the athlete's target; a no-op for non-KPI / unmapped metric
    // columns and when the athlete has no recorded benchmark (the `%` stays
    // literal).
    value = applyBenchmark(
      value,
      {
        metricType: metric.metricType,
        metricId: metric.metricId,
        benchmarkGridId: metric.benchmarkGridId,
        libraryExerciseId,
      },
      applyBenchmarkData,
      context.profileId,
    );
  }

  if (workoutHasErrors) {
    return (
      <ErrorContentWrapper
        className={className}
        useTooltip={true}
        disabled={disabled}
        errors={errorsForCell}
        showCollectionFlag={showCollectionFlag}
        displayValue={spec.displayValue(value)}
      />
    );
  }

  if (actualSetMetric) {
    // We have actuals data and the "Show Actuals" toggle is turned on
    // display in the format [actuals] /[prescribed]
    const actualParsed = spec.schema.safeParse(actualSetMetric.input);
    if (actualParsed.success) {
      const benchmarkData =
        applyBenchmarkData && context.profileId
          ? {
              libraryExerciseId,
              profileId: context.profileId,
              benchmarkGridId: metric.benchmarkGridId,
              metricId: metric.metricId,
              applyBenchmarkData,
            }
          : undefined;
      const delta = calculateDelta(
        m,
        metricValue?.input ?? '',
        actualSetMetric.input,
        benchmarkData,
      );
      // Orient the color by the metric's direction: for an inverse-scale metric
      // a lower actual (negative raw delta) is the improvement and reads green.
      const orientedDelta = applyInverseScale(
        delta,
        m.config?.isInverseScale ?? false,
      );
      const deltaClass =
        orientedDelta > 0
          ? 'bg-success-200'
          : orientedDelta < 0
            ? 'bg-error-200'
            : '';
      return (
        <div
          className={cn(className, deltaClass, 'flex justify-between gap-2')}
        >
          <Typography variant="body2">
            {spec.displayValue(actualParsed.data)}
          </Typography>
          <Typography className="mr-2" variant="body2">
            /{spec.displayValue(value)}
          </Typography>
        </div>
      );
    }
  }

  return (
    <div className={className}>
      {spec.displayValue(value)}
      {showCollectionFlag && <CollectionFlag />}
    </div>
  );
};
